import React, { useState } from "react";
import { TextField, Typography, Box, Autocomplete, Checkbox, FormControlLabel, Collapse, IconButton } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import styled from "@emotion/styled";
import MarkdownEditor from "../MarkdownEditor";
import TagPicker from "../TagPicker";

const FormField = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const ImportPanel = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
}));

const ImportHeader = styled(Box)({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    cursor: "pointer",
});

const ImportHelper = styled(Typography)(({ theme }) => ({
    display: "block",
    marginBottom: theme.spacing(1.5),
}));

const ImportRow = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const HelperBox = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const TemplateVarsLine = styled(Typography)(({ theme }) => ({
    display: "block",
    marginTop: theme.spacing(0.5),
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const templateVars = "{account_name} {account_slug} {month} {month_short} {month_number} {year} {year_short} {day} {quarter} {date}";

const StepBasics = ({
    form,
    onChange,
    onOverviewChange,
    onTagsChange,
    editorInitialOverview,
    mode,
    templateType,
    initialTemplate,
    overviewKey,
    showImport,
    importAccounts = [],
    importAccountId,
    onImportAccountChange,
    importSources = [],
    importSourceId,
    onImportSourcePick,
    importChildren,
    onImportChildrenChange,
    importSummary,
}) => {
    const typeLabel = templateType === "group" ? "Group" : "Project";
    const typeLabelLower = templateType === "group" ? "group" : "project";
    const [importOpen, setImportOpen] = useState(!!importSourceId);

    return (
        <>
            {showImport && (
                <ImportPanel>
                    <ImportHeader onClick={() => setImportOpen((prev) => !prev)}>
                        <Typography variant="subtitle2">Import from existing (optional)</Typography>
                        <IconButton size="small" aria-label={importOpen ? "Hide import options" : "Show import options"} aria-expanded={importOpen}>
                            {importOpen ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </ImportHeader>
                    <Collapse in={importOpen}>
                        <ImportHelper variant="caption" color="text.secondary">
                            Start from an existing {typeLabelLower} instead of a blank template. Its details are copied into the fields below, where you can edit everything before
                            saving. Leave blank to start from scratch.
                        </ImportHelper>
                        <ImportRow>
                            <Autocomplete
                                options={importAccounts}
                                getOptionLabel={(o) => o?.name || ""}
                                value={importAccounts.find((a) => a._id === importAccountId) || null}
                                onChange={(_, v) => onImportAccountChange(v ? v._id : "")}
                                isOptionEqualToValue={(o, v) => o._id === v._id}
                                renderInput={(params) => (
                                    <TextField {...params} label="Account" placeholder="Choose an account" helperText="Pick the account that owns the source." />
                                )}
                            />
                        </ImportRow>
                        <ImportRow>
                            <Autocomplete
                                options={importSources}
                                getOptionLabel={(o) => o?.name || ""}
                                value={importSources.find((s) => s._id === importSourceId) || null}
                                onChange={(_, v) => onImportSourcePick(v ? v._id : "")}
                                isOptionEqualToValue={(o, v) => o._id === v._id}
                                disabled={!importAccountId}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={`${typeLabel} to import from`}
                                        placeholder={importAccountId ? `Choose a ${typeLabelLower}` : "Choose an account first"}
                                        helperText={`Select the ${typeLabelLower} whose details you want to reuse.`}
                                    />
                                )}
                            />
                        </ImportRow>
                        {importSummary && (
                            <>
                                {importChildren ? (
                                    <ImportHelper variant="body2">
                                        Prefilled from the selected {typeLabelLower}. Saving will also import{" "}
                                        {templateType === "group" ? `${importSummary.projects} project(s), ` : ""}
                                        {importSummary.tasks} task(s), and {importSummary.files} file(s).
                                    </ImportHelper>
                                ) : (
                                    <ImportHelper variant="body2">
                                        Prefilled from the selected {typeLabelLower}. Only this {typeLabelLower} template will be created — its{" "}
                                        {templateType === "group" ? "projects and tasks" : "tasks"} won't be imported.
                                    </ImportHelper>
                                )}
                                <FormControlLabel
                                    control={<Checkbox checked={!!importChildren} onChange={(e) => onImportChildrenChange(e.target.checked)} />}
                                    label={templateType === "group" ? "Also import its projects and tasks" : "Also import its tasks"}
                                />
                            </>
                        )}
                    </Collapse>
                </ImportPanel>
            )}

            <FormField>
                <TextField
                    fullWidth
                    label="Display Name"
                    name="displayName"
                    value={form.displayName}
                    onChange={onChange}
                    placeholder="e.g. Monthly maintenance - Wordpress"
                    helperText="A friendly label shown in the template list. The Name field below is used when creating actual items."
                />
            </FormField>

            <FormField>
                <TextField
                    fullWidth
                    label="Name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    autoFocus
                    placeholder="e.g. {account_name} Monthly maintenance - {month} {year}"
                />
                <HelperBox>
                    <Typography variant="caption" color="text.secondary">
                        Template variables supported in name/overview. Use {"{custom_text}"} or {"{custom_*}"} for values prompted at execution time.
                    </Typography>
                    <TemplateVarsLine variant="caption">
                        {templateVars} {"{custom_text}"} {"{custom_yourname}"}
                    </TemplateVarsLine>
                </HelperBox>
            </FormField>

            <FormField>
                <SectionLabel variant="subtitle2">Overview</SectionLabel>
                <MarkdownEditor
                    key={`${mode}-${templateType}-${initialTemplate?._id || overviewKey || "new"}`}
                    initialContent={editorInitialOverview}
                    onChange={onOverviewChange}
                    placeholder="Template overview..."
                    autoFocus={false}
                />
            </FormField>

            <FormField>
                <TagPicker value={form.tags} onChange={onTagsChange} source="all" />
            </FormField>
        </>
    );
};

export default StepBasics;
